// Document numbering [MB D6; 02 §10, BR-NUM-001..004; 03 core.document_sequence]. The service
// interface lives here; the implementation (INSERT … ON CONFLICT + UPDATE … RETURNING inside the
// document transaction, never a max-plus-one lookup) lives in packages/db.
import { defineEnum } from '../enums/define.ts';
import type { EnumValue } from '../enums/define.ts';
import type { SequenceResetRule } from '../enums/types-platform.ts';
import type { BusinessDate } from '../kernel/date.ts';
import { AppError } from '../kernel/errors.ts';
import type { Id } from '../kernel/id.ts';

/**
 * 02 §10 doc types + 04 §16 #15 + `service_po` [assumption: po_type service needs numbers]; CR-W2a-39 adds the
 * FABRIC number (BR-FAB-001, numeric, no prefix), fabric tests (FT-) and roll barcodes (R + 8 digits, BR-LOT-003);
 * CR-W2b-12 adds assembly, label_run, sticker_run and sku_barcode; CR-BG2c adds inbound_shipment, requirement_run,
 * payment_run, bank_transfer and customer_deduction; CR-BG2d adds po_import (POI) and cut_report (CR-); CR-BG2e adds
 * sewing_allocation (AL-) and landed_cost (LC).
 */
export const DOC_TYPE = defineEnum('core.document_sequence.doc_type', [
  'sales_order',
  'cutting_slip',
  'fabric_po',
  'trim_po',
  'service_po',
  'goods_receipt',
  'fg_receipt',
  'shipment',
  'stock_adjustment',
  'transfer',
  'count',
  'debit_note',
  'credit_note',
  'customer_invoice',
  'journal',
  'ap_invoice_internal',
  'payment',
  'receipt',
  'bank_rec',
  'sage_batch',
  'lot',
  'style',
  'style_spec',
  'sample',
  'partner_customer',
  'partner_supplier',
  'partner_contractor',
  'cost_sheet',
  'actual_cost',
  'programme',
  'fabric',
  'fabric_test',
  'roll_barcode',
  // CR-W2b-12: warehouse assembly / disassembly (AS-, CR-W2b-1 R-CORE-1); label and sticker runs and SKU barcodes
  // (CR-W2b-10 R-CORE-1; their seeds borrowed `shipment`).
  'assembly',
  'label_run',
  'sticker_run',
  'sku_barcode',
  // CR-BG2c: inbound shipments (W2c.eta-freight R-CORE-1, IS-), requirement runs (W2c.planning R-CORE-PLN-2, MRP-),
  // payment runs (W2c.ap R-CORE-AP-1, PY), bank transfers and customer deductions (W2c.ar-bank R-PLT-AR-1, TR / DED).
  'inbound_shipment',
  'requirement_run',
  'payment_run',
  'bank_transfer',
  'customer_deduction',
  // CR-BG2d: customer PO imports (W2d.po-import R-POI-CORE-2, POI; were `POI` + identity) and cut reports (W2d.cutting
  // R-CORE-CUT-1, CR- + 6 digits; were a `cutting_slip` sequence row with code `cut_report`).
  'po_import',
  'cut_report',
  // CR-BG2e: sewing allocations (W2e.sewing-fg R-CORE-SEW-1, AL- + 6 digits; were a `cutting_slip` sequence row with code
  // `sewing_allocation`) and landed-cost charges (W2e.close R-CORE-CLS-3, LC; were `LC` + the row id).
  'sewing_allocation',
  'landed_cost',
]);
export type DocType = EnumValue<typeof DOC_TYPE>;

/** Opaque transaction handle; packages/db narrows it to its Drizzle transaction type. */
export interface TxHandle {
  readonly __tx: 'TxHandle';
}

export interface AllocatedNumber {
  /** Raw sequence value. */
  readonly number: number;
  /** Formatted document number shown and printed. */
  readonly display: string;
  readonly sequenceId: Id;
  readonly periodKey: string;
}

export interface DocumentNumberService {
  /** Allocates inside `tx`; a rollback returns the number (no gaps from failures). */
  allocate(
    tx: TxHandle,
    docType: DocType,
    opts: { readonly documentDate: BusinessDate; readonly scope?: string },
  ): Promise<AllocatedNumber>;
}

/** KIMCO fiscal year runs April–March and is named by its ending year (FY2028 starts 2027-04-01). */
export const FISCAL_YEAR_START_MONTH = 4;

export function fiscalYearOf(date: BusinessDate, startMonth = FISCAL_YEAR_START_MONTH): number {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  return startMonth === 1 ? y : m >= startMonth ? y + 1 : y;
}

/** `period_key` (part of the PK, so a reset is a new row): '-' | 'YYYY' | 'FYYYYY' | 'YYYY-MM'. */
export function periodKeyFor(rule: SequenceResetRule, date: BusinessDate): string {
  switch (rule) {
    case 'never':
      return '-';
    case 'calendar_year':
      return date.slice(0, 4);
    case 'fiscal_year':
      return `FY${fiscalYearOf(date)}`;
    case 'monthly':
      return date.slice(0, 7);
  }
}

export interface NumberFormat {
  /** Tokens: {prefix} {yyyy} {yy} {mm} {fy} {seq}; anything else is literal text. */
  readonly format: string;
  readonly prefix: string;
  readonly padding: number;
}

export const NUMBER_PADDING_DEFAULT = 5;
export const NUMBER_PREFIX_MAX = 10;

const TOKEN_RE = /\{([a-z]+)\}/g;
const TOKENS = new Set(['prefix', 'yyyy', 'yy', 'mm', 'fy', 'seq']);

export function validateNumberFormat(f: NumberFormat): void {
  const tokens = [...f.format.matchAll(TOKEN_RE)].map((m) => m[1] ?? '');
  const ok =
    f.prefix.length <= NUMBER_PREFIX_MAX &&
    Number.isInteger(f.padding) &&
    f.padding >= 1 &&
    f.padding <= 12 &&
    tokens.every((t) => TOKENS.has(t)) &&
    tokens.filter((t) => t === 'seq').length === 1;
  if (!ok) {
    throw new AppError('VALIDATION_FAILED', {
      fields: [
        {
          path: 'format',
          code: 'invalid_number_format',
          messageKey: 'errors.numbering.invalidFormat',
        },
      ],
    });
  }
}

/** Renders a document number, e.g. `{prefix}{yyyy}{seq}` + prefix "SO" + 42 → "SO202700042". */
export function formatDocumentNumber(
  f: NumberFormat,
  seq: number,
  documentDate: BusinessDate,
): string {
  validateNumberFormat(f);
  if (!Number.isSafeInteger(seq) || seq < 1)
    throw new RangeError('sequence must be a positive integer');
  const values: Record<string, string> = {
    prefix: f.prefix,
    yyyy: documentDate.slice(0, 4),
    yy: documentDate.slice(2, 4),
    mm: documentDate.slice(5, 7),
    fy: String(fiscalYearOf(documentDate)),
    seq: String(seq).padStart(f.padding, '0'),
  };
  return f.format.replace(TOKEN_RE, (_, t: string) => values[t] ?? '');
}
